import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';

const KENYAN_COUNTIES = [
  'Baringo', 'Bomet', 'Bungoma', 'Busia', 'Elgeyo-Marakwet',
  'Embu', 'Garissa', 'Homa Bay', 'Isiolo', 'Kajiado',
  'Kakamega', 'Kericho', 'Kiambu', 'Kilifi', 'Kirinyaga',
  'Kisii', 'Kisumu', 'Kitui', 'Kwale', 'Laikipia',
  'Lamu', 'Machakos', 'Makueni', 'Mandera', 'Marsabit',
  'Meru', 'Migori', 'Mombasa', 'Murang\'a', 'Nairobi',
  'Nakuru', 'Nandi', 'Narok', 'Nyamira', 'Nyandarua',
  'Nyeri', 'Samburu', 'Siaya', 'Taita-Taveta', 'Tana River',
  'Tharaka-Nithi', 'Trans Nzoia', 'Turkana', 'Uasin Gishu',
  'Vihiga', 'Wajir', 'West Pokot'
];

const LocationSelector = ({ value, onChange, error }) => {
  const [selectedCounty, setSelectedCounty] = useState('');
  const [cityInput, setCityInput] = useState('');
  const [countyTouched, setCountyTouched] = useState(false);

  // Parse existing value if provided
  useEffect(() => {
    if (value) {
      const parts = value?.split(' - ');
      if (parts?.[0]) setSelectedCounty(parts?.[0]);
      if (parts?.[1]) setCityInput(parts?.[1]);
    }
  }, []);

  const handleCountyChange = (e) => {
    const county = e?.target?.value;
    setSelectedCounty(county);
    setCountyTouched(true);
    setCityInput('');
    // Only emit value when county is selected (city is optional)
    if (county) {
      onChange(county);
    } else {
      onChange('');
    }
  };

  const handleCityChange = (e) => {
    const city = e?.target?.value;
    setCityInput(city);
    if (selectedCounty) {
      if (city?.trim()) {
        onChange(`${selectedCounty} - ${city?.trim()}`);
      } else {
        onChange(selectedCounty);
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* County Dropdown */}
      <div>
        <label htmlFor="county" className="block text-sm font-medium text-text-primary mb-2">
          County <span className="text-error">*</span>
        </label>
        <div className="relative">
          <select
            id="county"
            value={selectedCounty}
            onChange={handleCountyChange}
            onBlur={() => setCountyTouched(true)}
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth appearance-none bg-white ${
              error ? 'border-error' : 'border-border'
            }`}
          >
            <option value="">Select your county</option>
            {KENYAN_COUNTIES?.map((county) => (
              <option key={county} value={county}>
                {county}
              </option>
            ))}
          </select>
          <Icon name="ChevronDown" size={20} className="absolute right-3 top-3.5 text-text-secondary pointer-events-none" />
        </div>
        {error && !selectedCounty && (
          <p className="text-error text-xs mt-1">{error}</p>
        )}
      </div>

      {/* City/Town Text Input — appears after county is selected */}
      {selectedCounty && (
        <div
          style={{
            animation: 'fadeSlideIn 0.3s ease forwards'
          }}
        >
          <label htmlFor="city" className="block text-sm font-medium text-text-primary mb-2">
            City / Town
            <span className="text-text-secondary text-xs ml-1">(optional)</span>
          </label>
          <div className="relative">
            <input
              type="text"
              id="city"
              value={cityInput}
              onChange={handleCityChange}
              placeholder={`Enter your city or town in ${selectedCounty}`}
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth"
            />
            <Icon name="MapPin" size={20} className="absolute right-3 top-3.5 text-text-secondary pointer-events-none" />
          </div>
        </div>
      )}

      {/* Selected Location Display */}
      {value && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-3">
          <div className="flex items-center space-x-2">
            <Icon name="MapPin" size={16} className="text-primary" />
            <span className="text-sm font-medium text-primary">{value}</span>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default LocationSelector;